import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useBetaData } from '../../../contexts/BetaDataContext';
import { supabase } from '../../../supabaseClient';
import './TodoCreateModal.scss';

// ─── Custom Dropdown ──────────────────────
const CustomSelect = ({ value, onChange, options, placeholder, searchable = false }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    if (open && searchable) {
      setSearch('');
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open, searchable]);

  const selected = options.find((o) => o.value === value);

  const filtered = searchable && search.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(search.trim().toLowerCase()))
    : options;

  return (
    <div className={`custom-select ${open ? 'open' : ''}`} ref={ref}>
      <button
        type="button"
        className="select-trigger"
        onClick={() => setOpen(!open)}
      >
        {selected ? <span>{selected.label}</span> : (
          <span className="select-placeholder">{placeholder || '請選擇...'}</span>
        )}
        <svg className="select-arrow" width="12" height="8" viewBox="0 0 12 8" fill="none">
          <path d="M1 1.5L6 6.5L11 1.5" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="select-dropdown">
          {searchable && (
            <div className="select-search">
              <svg className="search-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#999" strokeWidth="1.5">
                <circle cx="6" cy="6" r="4" />
                <path d="M9.5 9.5l3 3" strokeLinecap="round" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                className="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="搜尋..."
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          {filtered.length === 0 ? (
            <div className="select-empty">找不到符合的結果</div>
          ) : (
            filtered.map((opt) => (
              <div
                key={opt.value}
                className={`select-option ${value === opt.value ? 'selected' : ''}`}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const TodoCreateModal = ({ isOpen, onClose, onSuccess }) => {
  const { profile, viewingCompanyId } = useAuth();
  const { myTickets, ensureMyTicketsLoaded } = useBetaData();
  const [title, setTitle] = useState('');
  const [ticketId, setTicketId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setTicketId('');
      ensureMyTicketsLoaded();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !profile?.id) return;

    setSubmitting(true);
    try {
      const insertData = {
        title: title.trim(),
        creator_id: profile.id,
        status: 'pending',
        completed: false,
        display_order: 0,
        department_id: profile.department_id || null,
        company_id: viewingCompanyId || profile.company_id || null,
      };

      if (ticketId) {
        insertData.ticket_id = parseInt(ticketId, 10);
      }

      const { data, error } = await supabase
        .from('items')
        .insert(insertData)
        .select('*, creator_profiles:creator_id(id, email)')
        .single();

      if (error) throw error;

      onSuccess?.(data);
      onClose();
    } catch (err) {
      console.error('Error creating todo:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const ticketOptions = [
    { value: '', label: '不關聯' },
    ...(myTickets || []).map((t) => ({
      value: String(t.id),
      label: `#${t.ticket_no} ${t.title}`,
    })),
  ];

  return (
    <div className="todo-create-overlay" onClick={onClose}>
      <div className="todo-create-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>建立個人 ToDo</h3>
          <button type="button" className="close-btn" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4l10 10M14 4L4 14" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>標題</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="例如：準備週會資料"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label>關聯 Ticket（選填）</label>
            <CustomSelect
              value={ticketId}
              onChange={setTicketId}
              options={ticketOptions}
              placeholder="不關聯"
              searchable
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              取消
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={!title.trim() || submitting}
            >
              {submitting ? '建立中...' : '建立'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TodoCreateModal;
